import React, { useEffect, useMemo, useRef, useState } from "react";
import Sortable from "sortablejs";
import { Modal, Input } from "antd";
import { X, Edit2, Trash2 } from "react-feather";

import ToggleSwitch from "../../../shared/ToggleSwitch/ToggleSwitch";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import ClipPath from "../../../../assets/icons/ic_clip-path.svg";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";
import type { BundledTranslationChanges } from "../Translations/RenameEntityModal";
import { TranslationField } from "../Translations/TranslationField";
import { useSportsbookTranslationEditor } from "../Translations/useSportsbookTranslationEditor";
import {
  getListEntityBaseName,
  toListTranslationEditorData,
  toTranslationValueEntries,
} from "../Translations/listTranslationData";

import type { FutureTab } from "./futuresDummyData";

interface FutureMarketTabsProps {
  tabs: FutureTab[];
  onAddTab: (name: string) => void | Promise<void>;
  onEditTab: (
    tabId: string,
    newName: string,
    translationChanges?: BundledTranslationChanges
  ) => void | Promise<void>;
  onToggleTab: (tabId: string, isActive: boolean) => void | Promise<void>;
  onDeleteTab: (tabId: string) => void | Promise<void>;
  onReorderTabs: (rows: FutureTab[]) => void;
  isAddingTab?: boolean;
  isEditingTab?: boolean;
  isDeletingTab?: boolean;
  togglingTabId?: string | null;
}

function destroySortable(instance: Sortable | null | undefined): void {
  try {
    instance?.destroy();
  } catch (error) {
    console.warn("Failed to destroy futures tabs Sortable", error);
  }
}

function FutureMarketTabs({
  tabs,
  onAddTab,
  onEditTab,
  onToggleTab,
  onDeleteTab,
  onReorderTabs,
  isAddingTab = false,
  isEditingTab = false,
  isDeletingTab = false,
  togglingTabId = null,
}: FutureMarketTabsProps): React.ReactElement {
  const [showAddTabModal, setShowAddTabModal] = useState(false);
  const [newTabName, setNewTabName] = useState("");
  const [editTabId, setEditTabId] = useState<string | null>(null);
  const [editTabName, setEditTabName] = useState("");
  const [originalEditTabName, setOriginalEditTabName] = useState("");
  const [deleteTabId, setDeleteTabId] = useState<string | null>(null);

  const tabsTbodyRef = useRef<HTMLTableSectionElement | null>(null);
  const tabsSortableRef = useRef<Sortable | null>(null);
  const { isEnabled: translationsFlagEnabled } = useProductFeatureFlag(
    "fm_translations",
    TRIVELTA_PRODUCTS.SPORTSBOOK
  );
  const translationsEnabled = translationsFlagEnabled === true;

  const editTab = editTabId
    ? tabs.find((tab) => tab.id === editTabId)
    : undefined;
  const translationEntityKey =
    translationsEnabled && editTabId
      ? (editTab?.translations?.key ?? editTab?.entity_id)
      : undefined;
  const initialTranslationData = useMemo(
    () =>
      translationsEnabled &&
      editTab &&
      toTranslationValueEntries(editTab).length > 0
        ? toListTranslationEditorData(
            editTab,
            translationEntityKey,
            editTab.ename
          )
        : undefined,
    [editTab, translationEntityKey, translationsEnabled]
  );
  const translationEditor = useSportsbookTranslationEditor({
    entityKey: translationEntityKey,
    enabled: translationsEnabled,
    initialData: initialTranslationData,
  });

  const deleteTabName = deleteTabId
    ? tabs.find((t) => t.id === deleteTabId)?.ename || ""
    : "";

  useEffect(() => {
    destroySortable(tabsSortableRef.current);
    tabsSortableRef.current = null;

    const tableBody = tabsTbodyRef.current;
    if (!tableBody || tabs.length === 0) return;

    tabsSortableRef.current = Sortable.create(tableBody, {
      animation: 150,
      handle: ".drag-handle",
      draggable: ".tab-row",
      onEnd: () => {
        const currentBody = tabsTbodyRef.current;
        if (!currentBody) return;

        const orderedIds = Array.from(
          currentBody.querySelectorAll<HTMLTableRowElement>(".tab-row")
        )
          .map((node) => node.getAttribute("data-tab-id"))
          .filter((id): id is string => Boolean(id));

        if (!orderedIds.length) return;

        const byId = new Map(tabs.map((tab) => [tab.id, tab]));
        const reordered = orderedIds
          .map((id, idx) => {
            const tab = byId.get(id);
            return tab ? { ...tab, order: String(idx + 1) } : null;
          })
          .filter((tab): tab is FutureTab => Boolean(tab));

        if (reordered.length === tabs.length) {
          onReorderTabs(reordered);
        }
      },
    });

    return () => {
      destroySortable(tabsSortableRef.current);
      tabsSortableRef.current = null;
    };
  }, [tabs, onReorderTabs]);

  const handleCreateTab = async () => {
    const name = newTabName.trim();
    if (!name || isAddingTab) return;
    try {
      await onAddTab(name);
      setShowAddTabModal(false);
      setNewTabName("");
    } catch {
      // Parent mutation already toasts the API error; keep the modal open.
    }
  };

  const handleOpenEditTab = (tab: FutureTab) => {
    const initialName = translationsEnabled
      ? getListEntityBaseName(tab, tab.ename)
      : tab.ename;
    setEditTabId(tab.id);
    setEditTabName(initialName);
    setOriginalEditTabName(initialName);
    translationEditor.reset();
  };

  const resetEditTabModal = () => {
    setEditTabId(null);
    setEditTabName("");
    setOriginalEditTabName("");
    translationEditor.reset();
  };

  const closeEditTabModal = () => {
    if (translationEditor.isMutating) return;
    if (translationEditor.hasDirtyTranslations) {
      translationEditor.showUnsavedChangesError();
      return;
    }
    resetEditTabModal();
  };

  const handleEditTab = async () => {
    const name = editTabName.trim();
    if (
      !editTabId ||
      !name ||
      isEditingTab ||
      translationEditor.isMutating ||
      translationEditor.hasEmptyTranslationDraft
    ) {
      return;
    }
    const dirtyValues = translationEditor.dirtyTranslationValues;
    const translationChanges =
      translationEditor.entityKey && Object.keys(dirtyValues).length > 0
        ? {
            translation_key: translationEditor.entityKey,
            translations: dirtyValues,
          }
        : undefined;

    if (name === originalEditTabName && !translationChanges) {
      resetEditTabModal();
      return;
    }

    try {
      await onEditTab(editTabId, name, translationChanges);
      resetEditTabModal();
    } catch {
      // Parent mutation already toasts the canonical rename error.
    }
  };

  const handleDeleteTab = async () => {
    if (!deleteTabId || isDeletingTab) return;
    try {
      await onDeleteTab(deleteTabId);
      setDeleteTabId(null);
    } catch {
      // Parent mutation already toasts the API error; keep the modal open.
    }
  };

  return (
    <div className="futures-market-tabs" data-testid="future-market-tabs">
      <div className="em-section-header">
        <h3>{AppStrings.FUTURE_MARKET_TABS}</h3>
        <button className="em-primary" onClick={() => setShowAddTabModal(true)}>
          {`+ ${AppStrings.ADD_TAB}`}
        </button>
      </div>
      <p className="em-section-subtitle">
        {AppStrings.MANAGE_FUTURE_TAB_TITLES}
      </p>

      <div className="em-table-wrapper">
        <table className="em-table" data-testid="future-tabs-table">
          <thead>
            <tr>
              <th>{`# ${AppStrings.ORDER}`}</th>
              <th>{AppStrings.TAB_NAME}</th>
              <th>{AppStrings.ACTIONS}</th>
            </tr>
          </thead>
          <tbody ref={tabsTbodyRef}>
            {tabs.length === 0 ? (
              <tr className="em-empty-row" data-testid="future-tabs-empty">
                <td colSpan={3} className="em-empty">
                  {AppStrings.NO_MARKET_TABS_FOUND_FOR_THIS_LEAGUE}
                </td>
              </tr>
            ) : (
              tabs.map((tab, idx) => (
                <tr
                  key={tab.id}
                  className="tab-row"
                  data-tab-id={tab.id}
                  data-testid={`future-tab-row-${tab.id}`}
                >
                  <td className="order-cell">
                    <div className="order-cell-inner">
                      <img
                        className="drag-handle"
                        src={ClipPath}
                        alt={AppStrings.DRAG}
                      />
                      <div
                        className="order-pill"
                        data-testid={`tab-order-${tab.id}`}
                      >
                        {idx + 1}
                      </div>
                    </div>
                  </td>
                  <td data-testid={`tab-name-${tab.id}`}>{tab.ename}</td>
                  <td className="actions">
                    <ToggleSwitch
                      status={tab.is_active !== false}
                      disabled={togglingTabId === tab.id}
                      ariaLabel={`${AppStrings.TAB_NAME}: ${tab.ename}`}
                      onChange={(checked) => onToggleTab(tab.id, checked)}
                    />
                    <button
                      type="button"
                      className="em-icon-btn"
                      title={AppStrings.EDIT_TAB}
                      data-testid={`edit-tab-${tab.id}`}
                      onClick={() => handleOpenEditTab(tab)}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      type="button"
                      className="em-icon-btn danger"
                      title={AppStrings.DELETE_TAB}
                      data-testid={`delete-tab-${tab.id}`}
                      onClick={() => setDeleteTabId(tab.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        closable={false}
        footer={null}
        width={600}
        open={showAddTabModal}
        onCancel={() => setShowAddTabModal(false)}
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">{AppStrings.ADD_MARKET_TAB}</h2>
            </div>
            <X
              onClick={() => setShowAddTabModal(false)}
              className="close-icon"
            />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <label htmlFor="futureTabName">{AppStrings.TAB_NAME}</label>
            <Input
              id="futureTabName"
              placeholder={AppStrings.ENTER_TAB_NAME}
              value={newTabName}
              onChange={(e) => setNewTabName(e.target.value)}
            />
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={() => setShowAddTabModal(false)}
            >
              {AppStrings.BACK}
            </button>
            <button
              className={`submit-button ${
                !newTabName.trim() || isAddingTab ? "disabled" : ""
              }`}
              disabled={!newTabName.trim() || isAddingTab}
              onClick={handleCreateTab}
            >
              {isAddingTab
                ? AppStrings.CREATING___
                : AppStrings.CREATE_BUTTON}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        closable={false}
        footer={null}
        width={480}
        className="future-tab-edit-modal"
        open={!!editTabId}
        onCancel={closeEditTabModal}
      >
        <div className="add-new-ip-panel future-tab-edit-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">{AppStrings.EDIT_MARKET_TAB}</h2>
            </div>
            <X
              onClick={closeEditTabModal}
              className="close-icon"
              aria-label={AppStrings.SPORTSBOOK_FM_CLOSE}
            />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="future-tab-edit-field">
              <label htmlFor="editFutureTabName">{AppStrings.TAB_NAME}</label>
              <Input
                id="editFutureTabName"
                placeholder={AppStrings.ENTER_TAB_NAME}
                value={editTabName}
                onChange={(e) => setEditTabName(e.target.value)}
              />
            </div>
            <TranslationField editor={translationEditor} />
          </div>
          <div className="modal-footer">
            <button className="cancel-button" onClick={closeEditTabModal}>
              {AppStrings.SPORTSBOOK_FM_CLOSE}
            </button>
            <button
              className={`submit-button ${
                !editTabName.trim() ||
                isEditingTab ||
                translationEditor.isMutating ||
                translationEditor.hasEmptyTranslationDraft
                  ? "disabled"
                  : ""
              }`}
              disabled={
                !editTabName.trim() ||
                isEditingTab ||
                translationEditor.isMutating ||
                translationEditor.hasEmptyTranslationDraft
              }
              aria-label={
                isEditingTab || translationEditor.isMutating
                  ? AppStrings.SAVING
                  : translationEditor.selectedLocale
                  ? formatString(
                      AppStrings.SPORTSBOOK_FM_SAVE_TRANSLATION_ARIA,
                      { locale: translationEditor.selectedLocale }
                    )
                  : AppStrings.SAVE
              }
              onClick={handleEditTab}
            >
              {isEditingTab || translationEditor.isMutating
                ? AppStrings.SAVING
                : AppStrings.SAVE}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        closable={false}
        footer={null}
        width={500}
        open={!!deleteTabId}
        onCancel={() => setDeleteTabId(null)}
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">{AppStrings.DELETE_MARKET_TAB}</h2>
            </div>
            <X onClick={() => setDeleteTabId(null)} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <p>
              {AppStrings.ARE_YOU_SURE_YOU_WANT_TO_DELETE_MARKET_TAB.replace(
                "{tabName}",
                deleteTabName || AppStrings.THIS_TAB
              )}
            </p>
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={() => setDeleteTabId(null)}
            >
              {AppStrings.BACK}
            </button>
            <button
              className={`submit-button delete-button ${
                isDeletingTab ? "disabled" : ""
              }`}
              disabled={isDeletingTab}
              onClick={handleDeleteTab}
            >
              {isDeletingTab ? AppStrings.DELETING___ : AppStrings.DELETE}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default FutureMarketTabs;
